<template>
  <div class="app-container">
    <FaqPanel
      page="payment"
      :top-offset="50"
      :max-width="350"
      :backdrop="true"
    />
    <el-container>
      <el-main>
        <MessagePopup
          v-model="dialogData"
          @close="dialogData.visible = false"
        />
        <div class="tab-container">
          <el-tabs v-model="activeTab">
            <el-tab-pane key="payments" label="Make a payment" name="payments">
              <el-row
                v-for="(rowItems, index) in chunk(
                  paymentMethods,
                  itemsInRow[colClass]
                )"
                :key="index"
                :gutter="10"
                class="cards-row"
              >
                <el-col
                  v-for="item in rowItems"
                  :key="item.sid"
                  :xs="24"
                  :sm="12"
                  :md="8"
                  :lg="6"
                  :xl="6"
                >
                  <PaymentMethodCard
                    :item="item.attributes"
                    :disabled="item.attributes.disabled"
                    :requirement="item.attributes.requirement"
                    :vat-payer="vatPayer"
                    :confirmation-data="item.attributes.payment_confirmation"
                    :is-staff="isStaff"
                    @submit="onSubmit"
                    @modal="openModal"
                  />
                </el-col>
              </el-row>
              <Modal
                :visible="showModal"
                :loading="loading"
                @close="closeModal"
                @go-back="onProfileSaved"
              />
              <el-row class="mt-1">
                <el-button
                  type="primary"
                  size="large"
                  @click="openSuggestionModal"
                >
                  Missing a payment option?
                </el-button>
              </el-row>
              <el-row class="mt-1">
                <el-col>
                  <el-alert
                    type="info"
                    effect="dark"
                    show-icon
                    :closable="false"
                  >
                    1 Euro = {{ eurUsdRate }} USD according to
                    <el-link
                      type="primary"
                      href="https://www.ecb.europa.eu/stats/policy_and_exchange_rates/euro_reference_exchange_rates/html/eurofxref-graph-usd.en.html"
                    >
                      European Central Bank
                    </el-link>
                  </el-alert>
                </el-col>
              </el-row>
            </el-tab-pane>
            <el-tab-pane
              :key="'transactions'"
              :label="'Payment history'"
              :name="'transactions'"
            >
              <keep-alive>
                <TransactionsTable
                  :items="itemsIncome"
                  :total="totalIncome"
                  :type="TransactionType.Added"
                  :with-invoice="true"
                  @update="update"
                />
              </keep-alive>
            </el-tab-pane>
          </el-tabs>
        </div>
      </el-main>
      <PaypalModal
        :visible="paypalModalVisible"
        :orderData="paypalOrderData"
        @close="closePaypalModal"
      />
    </el-container>
    <SuggestionForm :visible="suggestionModal" @close="closeSuggestionModal" />
  </div>
</template>

<script lang="ts">
import { computed, ref, reactive, defineComponent, watch } from 'vue'
import chunk from '@/utils/array'
import useResize from '@/hooks/useResize'
import PaymentMethodCard from './components/PaymentMethodCard.vue'
import TransactionsTable from '@/apps/traforama/views/transactions/components/TransactionsTable.vue'
import useValidation from '@/hooks/useValidation'
import { useReplenishmentsStore } from '@/store/modules/replenishments'
import { useConfigStore } from '@/store/modules/config'
import Modal from './components/Modal.vue'
import SuggestionForm from './components/SuggestionForm.vue'
import {
  IPaymentData,
  IReplenishmentResponseMeta
} from '@/interfaces/replenishments'
import { ElMessage } from 'element-plus'
import {
  TransactionType,
  VerificationType,
  PaymentValidationErrorsKey
} from '@/api/types'

import { useTransactionsStore } from '@/store/modules/transactions'
import { useUserStore } from '@/store/modules/user'

import { useContactStore } from '@/store/modules/contact'
import {
  itemsIncome,
  totalIncome
} from '@/apps/traforama/views/transactions/shared'
import MessagePopup, { DialogData } from './components/MessagePopup.vue'
import { safeSubmitProcessing } from '@/utils/helpers'
import { useReplenishmentMethodsStore } from '@/store/modules/replenishmentMethods'
import FaqPanel from '@/components/FaqPanel/Index.vue'
import RevolutCheckoutLoader from '@revolut/checkout'
import PaypalModal from './components/PaypalModal.vue'

export default defineComponent({
  name: 'Payment',
  components: {
    PaypalModal,
    TransactionsTable,
    PaymentMethodCard,
    MessagePopup,
    FaqPanel,
    Modal,
    SuggestionForm
  },
  setup(_props, context) {
    const transactionsStore = useTransactionsStore()
    const replenishmentsStore = useReplenishmentsStore()
    const replenishmentMethodsStore = useReplenishmentMethodsStore()
    const contactStore = useContactStore()
    const configStore = useConfigStore()
    const userStore = useUserStore()
    const showModal = ref(false)
    const paypalModalVisible = ref(false)
    const paypalOrderData = ref<IReplenishmentResponseMeta | null>(null)
    const suggestionModal = ref(false)

    const { colClass } = useResize()
    const processPaxum = function (response: IReplenishmentResponseMeta) {
      document.body.innerHTML += response.message
      const form = document.getElementById(
        'paxum_payment_finalized'
      ) as HTMLFormElement
      form.submit()
    }
    const redirectToPaymentSystem = function (
      response: IReplenishmentResponseMeta
    ) {
      window.location.href = response.message
    }

    const openRevolutCardModal = function (
      response: IReplenishmentResponseMeta
    ) {
      const data = response.message as any
      RevolutCheckoutLoader(data.token).then(function (instance) {
        const cardField = instance.payWithPopup({
          name: data.name,
          email: data.email
        })
      })
    }

    const openPaypalCardModal = function (
      response: IReplenishmentResponseMeta
    ) {
      paypalOrderData.value = response
      paypalModalVisible.value = true
    }

    const fetchContactQuietly = () => {
      contactStore.FetchContact({ skipGlobalErrorHandler: true })
    }

    replenishmentMethodsStore.FetchItems()
    fetchContactQuietly()

    const { provideValidation, setValidationErrors } = useValidation(
      PaymentValidationErrorsKey
    )
    const errors = provideValidation()

    const activeTab = ref('payments')
    const loading = ref(false)

    const eurUsdRate = computed(() => {
      return configStore.currentConfig.eur_to_usd
    })

    const paymentMethods = computed(
      () => replenishmentMethodsStore.currentItems
    )

    const dialogData = reactive<DialogData>({
      invoice: null,
      account: '',
      amount: null,
      currency: null,
      title: '',
      visible: false,
      message: '',
      otherActiveRequest: null
    })

    const openPopup = (data: IReplenishmentResponseMeta) => {
      dialogData.invoice = data.invoice
      dialogData.account = data.account
      dialogData.amount = data.amount
      dialogData.currency = data.currency
      dialogData.title = data.title
      dialogData.visible = true
      dialogData.message = data.message
      dialogData.otherActiveRequest = data.other_active_request
    }

    const itemsInRow = { xs: 1, sm: 2, md: 3, lg: 4, xl: 8 }

    const onSuccess = async (response: IReplenishmentResponseMeta) => {
      if (response.message === null) {
        ElMessage.error({
          message: 'Something went wrong, please contact administrator',
          showClose: true,
          duration: 10000
        })
        return
      }
      switch (response.payment_system_sid) {
        case 'paxum':
          processPaxum(response)
          break
        case 'paypal':
          openPaypalCardModal(response)
          break
        case 'capitalist':
          redirectToPaymentSystem(response)
          break
        case 'revolut':
          openRevolutCardModal(response)
          break
        default:
          openPopup(response)
      }
    }

    const onSubmit = async (payment: IPaymentData, loading: any) => {
      errors.value = {}
      await safeSubmitProcessing(
        loading,
        () => replenishmentsStore.SaveItem(payment),
        (response: IReplenishmentResponseMeta) => onSuccess(response),
        (err) => setValidationErrors(err, errors)
      )
    }

    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      transactionsStore.SetCurrentPage(newPage)
      transactionsStore.SetFilters({ type_id: TransactionType.Added })
      transactionsStore.FetchItems()
    }

    const vatPayer = computed(() => {
      return !!contactStore.contact?.vat_payer
    })

    const closeModal = () => {
      showModal.value = false
    }

    const closePaypalModal = () => {
      paypalModalVisible.value = false
    }

    const onProfileSaved = () => {
      replenishmentMethodsStore.FetchItems()
      fetchContactQuietly()
      closeModal()
    }

    const openModal = () => {
      showModal.value = true
    }

    watch(activeTab, (newValue, _oldValue) => {
      if (newValue === 'transactions') {
        update({})
      }
    })

    const openSuggestionModal = () => {
      suggestionModal.value = true
    }

    const closeSuggestionModal = () => {
      suggestionModal.value = false
    }

    const isStaff = computed(() => userStore.isStaff)

    return {
      TransactionType,
      chunk,
      colClass,
      itemsInRow,
      paymentMethods,
      loading,
      activeTab,
      onSubmit,
      update,
      VerificationType,
      itemsIncome,
      totalIncome,
      dialogData,
      vatPayer,
      paypalModalVisible,
      closePaypalModal,
      paypalOrderData,
      eurUsdRate,
      showModal,
      closeModal,
      openModal,
      onProfileSaved,
      suggestionModal,
      openSuggestionModal,
      closeSuggestionModal,
      isStaff
    }
  }
})
</script>
<style lang="scss" scoped>
.el-main {
  padding: 0 5px;
}
.cards-row .el-col {
  padding: 5px;
}
</style>
